<!--
  One person, the same card on every view of a person (Miles, 2026-10-23): main People, a group's
  People, and Tutoring's tutors. Literally this component. Every field is always here, in the same
  order; main People fills more of them (every group, the tutoring section, the overall total) and
  inside one group or Tutoring the others are left empty and hidden, because they'd only repeat it
  or don't apply. What someone may see is decided by the server and the page's permissions, never
  by a separate screen.

  Fields, in order (data-field): swatch, name, about, summary, total, progress, groups, spark,
  amount; opened: totals (spent, groups, tutoring, left), sources (each: source-label, rows,
  source-note, unpaid), message, statement, trace (main People, owner: why these lines are
  together), actions, extra.
-->
<script lang="ts">
  import type { Snippet } from "svelte";
  import { formatCents, formatMicro, type PersonOverview, type StatementEntry } from "@split/core";
  import BalanceAmount from "./BalanceAmount.svelte";
  import Swatch from "./Swatch.svelte";
  import Sparkline from "./charts/Sparkline.svelte";
  import { colorOf } from "../lib/colors.ts";
  import { shortDate, shortDateTime } from "../lib/format.ts";
  import { groupPath, link } from "../lib/router.svelte.ts";
  import { fill, shows } from "../settings/index.ts";
  import * as T from "../settings/people.ts";
  import {
    entryDate,
    figure,
    entryLine,
    rowsOf,
    signed,
    summaryText,
    tone,
    totalRows,
    totalText,
    type CardScope,
    type CardViewer,
    type TraceLine,
  } from "../lib/personCard.ts";

  let {
    overview,
    name,
    scope,
    viewer,
    open = false,
    ontoggle,
    color = null,
    status = "unpaid",
    progress = null,
    spark = null,
    groups = [],
    message = "",
    isYou = false,
    about = [],
    trace = [],
    onentry = null,
    entryAction = null,
    badges,
    actions,
    extra,
  }: {
    overview: PersonOverview;
    name: string;
    scope: CardScope;
    viewer: CardViewer;
    open?: boolean;
    ontoggle: () => void;
    /** Their color in the group (a group's card). */
    color?: string | null;
    status?: "paid" | "off" | "partial" | "unpaid";
    /** How much of their share is paid, 0 to 1 (a group's card). */
    progress?: number | null;
    /** Their balance over the last weeks (a group's card). */
    spark?: number[] | null;
    /** Which groups they're in, and Tutoring (main People). */
    groups?: string[];
    /** The reminder you can copy (a group's card, when they owe). */
    message?: string;
    /** It's you (shown with a dash, not counted). */
    isYou?: boolean;
    /** Lines about them under the name (Tutoring: their students, rate and hours taught). */
    about?: string[];
    /** Why each line is on this card (main People, owner only); empty hides it. */
    trace?: TraceLine[];
    /** Opening a statement line (a payment you can change); null: lines don't open. */
    onentry?: ((e: StatementEntry) => void) | null;
    /** A button on a statement line (a payout: "Not paid" / "Paid after all"), or null. */
    entryAction?: ((e: StatementEntry) => { label: string; title?: string; run: () => void } | null) | null;
    badges?: Snippet;
    actions?: Snippet;
    extra?: Snippet;
  } = $props();

  const summary = $derived(summaryText(overview, scope, viewer));
  const total = $derived(totalText(overview, scope));
  const first = $derived(overview.sources[0]);
  const totals = $derived(totalRows(overview, scope));
  const labelOf = (key: string) => overview.sources.find((s) => s.key === key)?.label ?? "";
  let all = $state(false);
  const lines = $derived(all ? overview.statement : overview.statement.slice(0, T.STMT_SHOWN));
  /** The balance after a line: everything on main People, that source's inside a group or Tutoring. */
  const after = (e: StatementEntry & { overall: number }) => (scope === "everyone" ? e.overall : e.running);
  const canOpen = (e: StatementEntry) => Boolean(onentry) && e.kind === "payment";
</script>

<div
  class="person"
  class:late={scope === "group" && first?.overdue === "late"}
  class:warn={scope === "group" && first?.overdue === "warn"}
  data-scope={scope}
>
  <button class="main" onclick={ontoggle} aria-expanded={open}>
    <span class="swatch-cell" data-field="swatch" hidden={!color}>
      {#if color}<Swatch {color} {status} size={34} />{/if}
    </span>
    <span class="who">
      <span class="name" data-field="name">{name}{@render badges?.()}</span>
      <span class="about" data-field="about" hidden={!about.filter(Boolean).length}>
        {#each about.filter(Boolean) as a, i (i)}<span class="line">{a}</span>{/each}
      </span>
      <span class="line" data-field="summary" hidden={!summary}>{summary}</span>
      <span class="line total" data-field="total" hidden={!total}>{total}</span>
      <span class="bar" data-field="progress" aria-hidden="true" hidden={progress === null}>
        <span style:width="{(progress ?? 0) * 100}%" style:background={color ? colorOf(color) : undefined}></span>
      </span>
      <span class="groups muted" data-field="groups" hidden={!groups.length}>{groups.join(" · ")}</span>
    </span>
    <span class="spark-cell" data-field="spark" hidden={!spark} title={T.SPARK_TITLE || undefined}>
      {#if spark}<Sparkline values={spark} color={color ? colorOf(color) : undefined} />{/if}
    </span>
    <span data-field="amount" class="amount-cell"><BalanceAmount cents={overview.totalCents} you={isYou} big /></span>
  </button>

  {#if open}
    <div class="detail">
      <dl class="totals" data-field="totals">
        {#each totals as t (t.key)}
          <div class:sum={t.key === "left"} data-field="total-{t.key}" hidden={t.hidden}><dt>{t.label}</dt><dd class="num {t.tone}">{t.value}</dd></div>
        {/each}
      </dl>

      <div class="sources" data-field="sources" aria-label={fill(T.CARD_SOURCES_LABEL, { name })}>
        {#each overview.sources as s (s.key)}
          <section class="source" data-field="source">
            <h3 class="source-label" data-field="source-label" hidden={scope !== "everyone"}>
              {#if s.groupId && viewer.side === "owner"}
                <a href={groupPath(s.groupId, "people")} use:link>{s.label}</a>
              {:else if s.groupId}
                <a href={groupPath(s.groupId)} use:link>{s.label}</a>
              {:else}
                <span>{s.label}</span>
              {/if}
              <span class="num {tone(s.balanceCents)}">{s.you ? "" : figure(s.balanceCents)}</span>
            </h3>
            <dl class="nums" data-field="rows">
              {#each rowsOf(s, viewer) as r (r.key)}
                <div class:sum={r.key === "balance"}><dt>{r.label}</dt><dd class="num {r.tone}">{r.amount}</dd></div>
              {/each}
            </dl>
            <ul class="unpaid" data-field="unpaid" hidden={!s.unpaid.length}>
              {#each s.unpaid as u (u.itemId)}
                <li><span>{u.name} <span class="muted">{shortDate(u.date)}</span></span><span class="num">{formatMicro(u.remainingMicro)}{u.partial ? T.UNPAID_LEFT : ""}</span></li>
              {/each}
            </ul>
          </section>
        {/each}
      </div>

      <p class="msg" data-field="message" hidden={!message}>{message}</p>

      <section class="statement" data-field="statement" hidden={!overview.statement.length}>
        {#if shows(T.STMT_TITLE)}<h3 class="hist-h">{T.STMT_TITLE}</h3>{/if}
        <ul class="hist" aria-label={fill(T.STMT_LABEL, { name })}>
          {#each lines as e (e.source + e.kind + e.id)}
            {@const act = entryAction?.(e) ?? null}
            <li class="hrow">
              {#if canOpen(e)}
                <button class="what open" onclick={() => onentry?.(e)} title={T.STMT_EDIT_TITLE}>
                  <span>{entryDate(e)} · {entryLine(e, labelOf(e.source), viewer)}</span>
                </button>
              {:else}
                <span class="what">
                  <span>{entryDate(e)} · {entryLine(e, labelOf(e.source), viewer)}</span>
                </span>
              {/if}
              <span class="num delta" class:in={e.cents < 0}>{signed(e.cents)}</span>
              <span class="num run muted">{figure(after(e))}</span>
              {#if act}<button class="btn small quiet act" title={act.title} onclick={act.run}>{act.label}</button>{/if}
            </li>
          {/each}
        </ul>
        {#if !all && overview.statement.length > T.STMT_SHOWN}
          <button class="btn small quiet" onclick={() => (all = true)}>{fill(T.STMT_SHOW_ALL, { count: overview.statement.length })}</button>
        {/if}
      </section>

      <details class="trace" data-field="trace" hidden={!trace.length || !shows(T.TRACE_TITLE)}>
        <summary>{T.TRACE_TITLE}</summary>
        <ul>
          {#each trace as t, i (i)}
            <li>
              <span>{t.line}</span>
              <span class="muted">{t.why}</span>
              {#if t.left}<span class="left">{t.left}</span>{/if}
            </li>
          {/each}
        </ul>
      </details>

      <div class="actions-wrap" data-field="actions">{@render actions?.()}</div>
      <div data-field="extra">{@render extra?.()}</div>
    </div>
  {/if}
</div>

<style>
  [hidden] {
    display: none !important;
  }
  .person {
    background: var(--surface);
  }
  .main {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 16px;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .main:hover {
    background: var(--wash);
  }
  .who {
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 3px;
  }
  .name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-weight: 650;
    font-size: var(--t-lg);
  }
  .line {
    color: var(--ink-2);
    font-size: var(--t-sm);
  }
  .line.total {
    color: var(--ink);
    font-weight: 600;
  }
  .warn .line[data-field="summary"] {
    color: color-mix(in srgb, var(--series-4) 70%, var(--ink));
  }
  .late .line[data-field="summary"] {
    color: var(--owes);
  }
  .groups {
    font-size: var(--t-xs);
  }
  /* Badges next to the name (each page says which: an account, invited, club treasurer). */
  .name :global(.badge) {
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--wash);
    color: var(--muted);
    font-size: var(--t-xs);
    font-weight: 600;
  }
  .name :global(.badge.ok) {
    color: var(--ok);
  }
  .bar {
    display: block;
    height: 4px;
    margin-top: 4px;
    border-radius: 2px;
    background: var(--wash);
    overflow: hidden;
    max-width: 280px;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--ink-2);
    border-radius: 2px;
    transition: width 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @media (prefers-reduced-motion: reduce) {
    .bar span {
      transition: none;
    }
  }
  .spark-cell {
    width: 140px;
    display: flex;
    justify-content: center;
  }
  @media (max-width: 640px) {
    .spark-cell {
      display: none;
    }
  }
  .detail {
    padding: 0 16px 16px;
    display: grid;
    gap: 12px;
  }
  .sources {
    display: grid;
    gap: 12px;
  }
  .source {
    display: grid;
    gap: 6px;
  }
  .source-label {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin: 0;
    font-size: var(--t-sm);
    font-weight: 650;
  }
  .nums,
  .totals {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
  }
  .nums dt,
  .totals dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }
  .nums dd,
  .totals dd {
    margin: 0;
    font-weight: 600;
  }
  .about {
    display: grid;
  }
  .totals .sum dd,
  .nums .sum dd {
    font-weight: 700;
  }
  .unpaid {
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: 1px solid var(--rule);
  }
  .unpaid li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid var(--rule);
    font-size: var(--t-sm);
  }
  .msg {
    margin: 0;
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--wash);
    font-size: var(--t-sm);
    user-select: all;
  }
  .hist-h {
    margin: 2px 0 4px;
    font-size: var(--t-sm);
  }
  .hist {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .hrow {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto auto;
    align-items: baseline;
    gap: 4px 12px;
    padding: 6px 0;
    border-top: 1px solid var(--rule);
    font-size: var(--t-sm);
  }
  .what {
    display: grid;
    min-width: 0;
    text-align: left;
  }
  button.what {
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
  button.what:hover span {
    text-decoration: underline;
  }
  .delta.in {
    color: var(--ok);
  }
  /* A figure's color, always with its sign: minus and red they owe, plus and green a credit. */
  .owes {
    color: var(--owes);
  }
  .credit {
    color: var(--ok);
  }
  .trace {
    font-size: var(--t-sm);
  }
  .trace summary {
    cursor: pointer;
    color: var(--ink-2);
  }
  .trace ul {
    list-style: none;
    margin: 6px 0 0;
    padding: 0;
    display: grid;
    gap: 6px;
  }
  .trace li {
    display: grid;
    gap: 1px;
  }
  .trace .left {
    color: var(--owes);
  }
  .amount-cell {
    flex: none;
    text-align: right;
  }
  .run {
    font-size: var(--t-xs);
    min-width: 0;
  }
  @media (max-width: 520px) {
    .hrow {
      grid-template-columns: minmax(0, 1fr) auto;
    }
    .run {
      grid-column: 1 / -1;
      text-align: right;
    }
  }
  .small {
    font-size: var(--t-sm);
  }
  p {
    margin: 0;
  }
</style>
